import { useEffect, useState } from 'react'
import { Outlet, useSearchParams } from 'react-router-dom'
import { useDealCounts } from '../../api/queries/deals'
import type { DealFilters } from '../../api/queries/deals'
import { useActiveJobCount } from '../../api/queries/jobs'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { EmptyState, ErrorState, Skeleton } from '../../ui/Feedback'
import { TextField } from '../../ui/TextField'
import { parseBoardView, serialiseBoardView } from '../../lib/boardView'
import type { BoardView } from '../../lib/boardView'
import { ArchivedList } from './ArchivedList'
import { Board } from './Board'
import { SummaryTiles } from './SummaryTiles'
import { InviteButton } from '../invite/InviteButton'
import { UploadDialog } from '../upload/UploadDialog'
import { shellStyles } from '../../app/AppShell'
import styles from './PipelinePage.module.css'

const FILTERS: ReadonlyArray<{ value: BoardView['priority']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High priority' },
  { value: 'medium', label: 'Medium priority' },
  { value: 'low', label: 'Low priority' },
  { value: 'review', label: 'Re-evaluation needed' },
]

/** Long enough that a typed word is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250

export function PipelinePage() {
  const [params, setParams] = useSearchParams()
  const [uploading, setUploading] = useState(false)
  const view = parseBoardView(params)
  const jobs = useActiveJobCount()

  // The URL stays the single source of truth for the view, but a keystroke must
  // not be a request: the field holds what is being typed, and the URL catches
  // up when the typing stops.
  const [typed, setTyped] = useState(view.search)
  useEffect(() => {
    if (typed === view.search) return
    const timer = setTimeout(() => update({ search: typed }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed])

  const filters: DealFilters = { search: view.search, archived: view.archived, priority: view.priority }
  const counts = useDealCounts(filters)

  function update(patch: Partial<BoardView>) {
    setParams(serialiseBoardView({ ...view, ...patch }), { replace: true })
  }

  return (
    <>
      <header className={shellStyles.topbar}>
        <div className={styles.search}>
          <TextField
            label="Search deals"
            hideLabel
            type="search"
            placeholder="Search deals…"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
        <div className={shellStyles.topbarRight}>
          <InviteButton />
          <Button variant="primary" onClick={() => setUploading(true)}>
            + New Transcript
          </Button>
        </div>
      </header>

      <main className={shellStyles.content}>
        <h1 className="dp-sr-only">Pipeline</h1>

        {counts.isPending ? (
          <div className={styles.loading}>
            <div className={styles.tileRow}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={62} />
              ))}
            </div>
            <div className={styles.columns}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={180} />
              ))}
            </div>
          </div>
        ) : counts.isError ? (
          <ErrorState
            title="Could not load your pipeline"
            body={userFacingMessage(counts.error)}
            action={<Button onClick={() => void counts.refetch()}>Try again</Button>}
          />
        ) : (
          <>
            <SummaryTiles
              countsByStage={counts.data?.countsByStage ?? {}}
              reEvaluationCount={counts.data?.reEvaluationCount ?? 0}
              activeJobCount={jobs.data ?? 0}
              jobsUnavailable={jobs.isError}
            />

            {/* One row: what the columns are, what is filtered, and the archive
                switch at the far end — the two questions labelled, as in the
                redesign, rather than six unlabelled controls in two rows. */}
            <div className={styles.controls}>
              {view.archived ? null : (
                <>
                  <span className={styles.controlLabel}>Group</span>
                  <div className={styles.grouping} role="group" aria-label="Group deals by">
                    <Button
                      aria-pressed={view.grouping === 'stage'}
                      className={view.grouping === 'stage' ? styles.groupOn : undefined}
                      onClick={() => update({ grouping: 'stage' })}
                    >
                      By stage
                    </Button>
                    <Button
                      aria-pressed={view.grouping === 'decision'}
                      className={view.grouping === 'decision' ? styles.groupOn : undefined}
                      onClick={() => update({ grouping: 'decision' })}
                    >
                      By decision
                    </Button>
                  </div>

                  <span className={styles.divider} aria-hidden="true" />

                  <span className={styles.controlLabel}>Priority</span>
                  <div className={styles.filters} role="group" aria-label="Filter deals">
                    {FILTERS.map((filter) => (
                      <button
                        key={filter.value}
                        type="button"
                        aria-pressed={view.priority === filter.value}
                        className={`${styles.chip} ${view.priority === filter.value ? styles.chipOn : ''}`}
                        onClick={() => update({ priority: filter.value })}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* A switch, not a chip: it turns the archive on, it does not pick
                  one of several alternatives. */}
              <button
                type="button"
                role="switch"
                aria-checked={view.archived}
                className={styles.archiveSwitch}
                onClick={() => update({ archived: !view.archived })}
              >
                <span className={styles.track} aria-hidden="true">
                  <span className={styles.knob} />
                </span>
                Show archived
              </button>
            </div>

            <p className={styles.hint}>
              {view.archived
                ? 'Showing archived deals only.'
                : view.grouping === 'stage'
                  ? 'Grouped by stage — how far the conversation has moved, as assessed by the backend.'
                  : 'Grouped by your recorded decision.'}
            </p>

            {view.archived ? (
              <ArchivedList filters={filters} />
            ) : counts.data && counts.data.total === 0 ? (
              <EmptyState
                title="No deals match this view"
                body="Clear the filters, or analyse a call to add a deal to your pipeline."
              />
            ) : (
              <Board filters={filters} grouping={view.grouping} />
            )}
          </>
        )}
      </main>
      <Outlet />
      {uploading ? <UploadDialog onClose={() => setUploading(false)} /> : null}
    </>
  )
}
